import React, { useContext, useEffect, useState } from 'react'
import { Loader } from '../common/spinner/Loader'
import { getProcessOwnersColumns, mapWorkplacesSNIs } from '../../helpers/settingsHelper'
import { Process, ProcessOwner, ProcessOwnerDto, Workplace } from '../../api/schemas/schema'
import { DataContext } from '../../context/DataContext'
import { api } from '../../helpers/apiHelper'
import { handleMessage } from '../../helpers/stateHelper'
import { t } from '../../i18n/i18n'
import { ModalSettings } from './ModalSettings'
import { IOpenCloseModalProps } from '../../interfaces/IOpenCloseModalProps'
import { makeStyles, tokens, Button } from '@fluentui/react-components'
import { AddCircle24Regular } from '@fluentui/react-icons'
import { ProcessOwnerAvatarOutput } from '../common/inputs/ProcessOwnerAvatarOutput'
import { CustomList } from '../common/list/CustomList'
import { InfoMessageBar } from '../common/other/InfoMessageBar'
import { Text } from '../common/text/Text'
import { ModalUpsertProcessOwner } from './ModalUpsertProcessOwner'

const useStyles = makeStyles({
   topContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeighteight: tokens.spacingVerticalXXXL,
      marginBottom: tokens.spacingVerticalL,
      marginRight: tokens.spacingHorizontalS,

      '@media screen and (max-width: 1007px)': {
         flexDirection: 'column',
         padding: tokens.spacingVerticalM,
      },
   },
   addButtonContainer: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
   addButtonPhone: {
      '@media screen and (max-width: 1007px)': {
         width: '100%',
      },
   },
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   row: {
      '& >': {
         ':nth-child(1)': {
            width: '25%',
         },
         ':nth-child(2)': {
            width: '15%',
            minWidth: '150px',
         },
         ':nth-child(3)': {
            width: '25%',
            minWidth: '250px',
         },
      },
   },
   phoneItem: {
      display: 'none',
      '@media screen and (max-width: 1007px)': {
         display: 'flex',
      },
      flexDirection: 'column',
      padding: `0 ${tokens.spacingHorizontalS}`,
      borderBottom: `1px solid ${tokens.colorBrandStroke2}`,
      paddingBottom: tokens.spacingVerticalL,
      '& > *': {
         marginBottom: tokens.spacingVerticalXXS,
      },
   },
   scrollContainer: {
      height: '30vh',
      overflowY: 'scroll',
      '@media screen and (min-height: 700px)': {
         height: '45vh',
      },
      '@media screen and (min-height: 1000px)': {
         height: '70vh',
      },
   },
})

export const ModalProcessOwner = ({ open, closeModal }: IOpenCloseModalProps) => {
   const classes = useStyles()
   const dataContext = useContext(DataContext)
   const { hasMsalToken, organisation } = dataContext.state
   const [allProcesses, setAllProcesses] = useState<Process[]>([])
   const [allWorkplaces, setAllWorkplaces] = useState<Workplace[]>([])
   const [allProcessOwners, setAllProcessOwners] = useState<ProcessOwnerDto[]>([])
   const [loading, setLoading] = useState(true)
   const [openModal, setOpenModal] = useState(false)
   const [selectedItem, setSelectedItem] = useState<ProcessOwner>(null)

   // Commented away (both in frontend and backend) as we need to specify what or if process oweners can be "in use" to not be deleted
   // const [processOwnersInUseMap, setProcessOwnersInUseMap] = useState<Record<string, boolean>>({})

   // useEffect(() => {
   //    getProcessOwnerUsage(allProcessOwners, setProcessOwnersInUseMap)
   // }, [allProcessOwners])

   const onRowClick = (item: Workplace) => {
      setOpenModal(true)
      setSelectedItem(item)
   }

   useEffect(() => {
      if (!open) return
      ;(async () => {
         try {
            const [processesResponse, allSnis, workplacesResponse, allProcessOwners] = await Promise.all([
               api().getAllProcesss(),
               (await api().getAllSnis()).data,
               api().getWorkplacesByOrganisationId(organisation.id),
               (await api().getAllProcessOwnersByOrganisationId(organisation.id)).data,
            ])
            const mappedWorkplaces = mapWorkplacesSNIs(workplacesResponse.data, allSnis)

            setAllProcesses(processesResponse.data)
            setAllWorkplaces(mappedWorkplaces)
            setAllProcessOwners(allProcessOwners)
         } catch (error) {
            handleMessage(dataContext.setRootState, 'error', t('SomethingWentWrong'), t('CouldNotFetchData'))
         } finally {
            setLoading(false)
         }
      })()
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [open, organisation.id])

   return (
      <>
         <ModalSettings open={open} closeModal={closeModal} title={t('ProcessOwner')}>
            <div className={classes.topContainer}>
               <Text size={400}>{t('ProcessOwnerInfo')}</Text>
               <div className={classes.addButtonContainer}>
                  <Button
                     onClick={() => onRowClick(null)}
                     appearance="primary"
                     disabled={false}
                     icon={<AddCircle24Regular />}
                     className={classes.addButtonPhone}
                  >
                     {t('Add')}
                  </Button>
               </div>
            </div>

            {loading ? (
               <Loader />
            ) : allProcessOwners.length > 0 ? (
               <>
                  <CustomList
                     className={classes.hideInPhone}
                     columns={getProcessOwnersColumns()}
                     items={allProcessOwners}
                     onRowClick={onRowClick}
                     rowClassName={classes.row}
                  />
                  <div className={classes.scrollContainer}>
                     {allProcessOwners?.map((item, index) => (
                        <div className={classes.phoneItem} key={index} onClick={() => onRowClick(item)}>
                           <ProcessOwnerAvatarOutput bold processOwner={item} />
                           {item.processes?.length > 0 && (
                              <>
                                 <Text size={400} weight="semibold">
                                    {t('Processes')}
                                 </Text>
                                 <Text size={400}>{item.processes.map((p: Process) => p.text).join(', ')}</Text>
                              </>
                           )}
                           {item.workplaces?.length > 0 && (
                              <>
                                 <Text size={400} weight="semibold">
                                    {t('Workplaces')}
                                 </Text>
                                 <Text size={400}>{item.workplaces.map((w: Workplace) => w.name).join(', ')}</Text>
                              </>
                           )}
                        </div>
                     ))}
                  </div>
               </>
            ) : (
               <InfoMessageBar title={t('TimeToCreateYourFirstItem', { Item: t('ProcessOwner').toLowerCase() })} />
            )}
         </ModalSettings>

         <ModalUpsertProcessOwner
            open={openModal}
            setOpen={setOpenModal}
            setAllProcessOwners={setAllProcessOwners}
            processOwner={selectedItem}
            allWorkplaces={allWorkplaces}
            allProcesses={allProcesses}
            allProcessOwners={allProcessOwners}

            // processOwnersInUseMap={processOwnersInUseMap}
         />
      </>
   )
}
